import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

import * as Api from "@modules/api";
import {
  cancelQuiz,
  completeQuiz,
  getQuestionsAndAnswers,
  getQuizInstructions,
  setAlert,
  startQuiz,
  submitAnswer,
  useAppSelector,
  useCompleteQuiz,
  useGetQuestionsAndAnswers,
  useGetQuizInstructions,
  useStartQuiz,
  useSubmitAnswer,
} from "@lib/stores";
import { QuizIcon } from "@components/icons/QuizIcon";
import TranslatedText from "@components/items/display/TranslatedText";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import MarkdownView from "react-showdown";
import { SVG } from "@components/items/common/SVG";
import BasicSlider from "@components/items/display/Slider";
import { Separator } from "@ui/separator";
import QuizOption from "@components/layouts/b2b/quiz/QuizOption";
import BasicCountdown from "@components/items/display/Countdown";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { convertMsToHourMinSec } from "@utils/helpers/timeFormtter";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";

export const SkillQuizPage = () => {
  const navigate = useAppNavigate();

  const { skillId } = useParams();

  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);

  const { data: quizInstructionsData } = useGetQuizInstructions();
  const { data: startQuizData } = useStartQuiz();
  const { data: completeQuizData } = useCompleteQuiz();

  useEffect(() => {
    if (skillId) {
      getQuizInstructions(skillId);
    }
  }, [skillId]);

  return (
    <div className="cus-scrollbar-w-none h-full overflow-y-auto p-[80px]">
      {!isQuizStarted && quizInstructionsData?.text ? (
        !isQuizCompleted ? (
          <QuizInstructions
            text={quizInstructionsData.text}
            onQuizStart={() => {
              startQuiz({ contentEntityId: skillId });

              setIsQuizStarted(true);
            }}
          />
        ) : (
          <QuizComplete data={completeQuizData} />
        )
      ) : startQuizData && !isQuizCompleted ? (
        <QuizContent
          content={startQuizData}
          onQuizCancel={() => {
            setIsQuizStarted(false);
          }}
          onQuizComplete={(data) => {
            console.log("completed");

            if (!data.isSuccessful) {
              setAlert({
                show: true,
                for: "page",
                type: AlertType.WARNING,
                messages: [
                  {
                    code: 0,
                    field: null,
                    message: translate("skill-details.quiz.fail message"),
                  },
                ],
              });

              navigate(`/details/${skillId}/`);
            } else {
              setIsQuizStarted(false);
              setIsQuizCompleted(true);
            }
          }}
        />
      ) : null}
    </div>
  );
};

interface QuizInstructionsProps {
  text: string;
  onQuizStart: () => void;
}

const QuizInstructions = ({ text, onQuizStart }: QuizInstructionsProps) => {
  return (
    <div className="w-full">
      <div className="flex items-center justify-start gap-3 text-[36px] font-medium text-black dark:text-white">
        <QuizIcon width={27} height={30} />
        <TranslatedText text="skill-details.quiz.quiz" />
      </div>
      <div className="mt-[32px] text-[17px] font-normal text-black dark:text-white">
        <TranslatedText text="skill-details.quiz.description" />
        <div className="mt-[27px] text-[16px] font-bold text-b2b-primary">
          <TranslatedText text="skill-details.quiz.instructions" />
        </div>
        <div className="mt-[14px] text-[15px] text-b2b-primary">
          <MarkdownView
            markdown={text}
            options={{ tables: true, emoji: true }}
          />
        </div>
      </div>
      <div className="mt-[80px] flex items-center justify-center">
        <ShadcnButton
          onClick={onQuizStart}
          className="self-center bg-b2b-primary px-12 text-white hover:bg-b2b-primary/85"
        >
          <TranslatedText text={`skill-details.quiz.${"start quiz"}`} />
        </ShadcnButton>
      </div>
    </div>
  );
};

interface QuizContentProps {
  content: Api.Server.StartQuizTryOutput;
  onQuizCancel: () => void;
  onQuizComplete: (
    data: Api.Server.GetQuizResultWithCertificatesOutput
  ) => void;
}

const QuizContent = ({
  content,
  onQuizCancel,
  onQuizComplete,
}: QuizContentProps) => {
  const [selectedOption, setSelectedOption] =
    useState<Api.Server.UserQuizAnswerOutput | null>(null);
  const [questionNum, setQuestionNum] = useState(1);

  const totalQuestions = content.questions?.length;
  const options = content.questions?.[questionNum - 1].answers;
  const questionText = content.questions?.[questionNum - 1].text;

  const { status: submitAnswerStatus } = useSubmitAnswer();
  const { status: completeQuizStatus } = useCompleteQuiz();

  const isLoading =
    submitAnswerStatus === "loading" || completeQuizStatus === "loading";

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-y-6 p-5">
        <div
          dir="rtl"
          className="flex w-full items-center justify-start gap-x-4 px-5"
        >
          <SVG
            src="/assets/icons/close.svg"
            preProcessor={(code: string) =>
              code
                .replace(/(fill=)"(?!none")[^"]+"/g, 'fill="currentColor"')
                .replace(/(stroke=)"(?!none")[^"]+"/g, 'stroke="currentColor"')
            }
            className="cursor-pointer text-b2b-primary"
            onClick={() => {
              cancelQuiz({ quizTryId: content.quizTryId }, onQuizCancel);
            }}
          />
          <BasicSlider
            thumbClassName="bg-white !border-b2b-primary h-[39px] w-[59px] flex justify-center items-center p-5 border-[4px]"
            thumbContent={
              <div className="text-[15px] font-medium text-b2b-primary">
                {questionNum}/{totalQuestions}
              </div>
            }
            rangeClassName="bg-b2b-primary"
            value={[(questionNum / (totalQuestions || 0)) * 100]}
            disabled={true}
            inverted
          />
        </div>
        <Separator />
        <div className="rounded-[30px] border border-b2b-primary px-12 py-6 text-[17px] font-bold text-b2b-primary">
          {questionText}
        </div>
        <div className="flex w-[800px] flex-col gap-y-5">
          {options?.map((o, index) => (
            <QuizOption
              key={index}
              no={index + 1}
              text={o.text || ""}
              isSelected={selectedOption?.answerId === o.answerId}
              onClick={() => setSelectedOption(o)}
            />
          ))}
        </div>
        <div className="flex w-full items-center justify-between">
          <ShadcnButton
            disabled={selectedOption === null || isLoading}
            className="mx-auto mt-auto bg-b2b-primary px-10 text-[18px] text-white hover:bg-b2b-primary/85"
            onClick={() => {
              submitAnswer(
                {
                  quizTryId: content.quizTryId,
                  questionId: content.questions?.[questionNum - 1].questionId,
                  answerId: selectedOption?.answerId,
                },
                () => {
                  console.log(questionNum, content.questions?.length);

                  if (questionNum === content.questions?.length) {
                    completeQuiz(
                      { quizTryId: content.quizTryId },
                      onQuizComplete
                    );
                  } else {
                    setQuestionNum((questionNum) => ++questionNum);
                    setSelectedOption(null);
                  }
                }
              );
            }}
            isLoading={isLoading}
          >
            <TranslatedText
              text={
                questionNum !== content.questions?.length
                  ? "skill-details.quiz.continue"
                  : "skill-details.quiz.finish"
              }
            />
          </ShadcnButton>
          <BasicCountdown
            key={questionNum.toString()}
            color="#1F2F63"
            durationInSeconds={45}
            counterSize={100}
            strokeWidth={8}
            customChildStyle="text-[15px] font-medium text-black dark:text-white"
            onComplete={() => {
              completeQuiz({ quizTryId: content.quizTryId }, onQuizComplete);
            }}
          />
        </div>
      </div>
    </div>
  );
};

interface QuizCompleteProps {
  data: Api.Server.GetQuizResultWithCertificatesOutput | null;
}

const QuizComplete = ({ data }: QuizCompleteProps) => {
  const navigate = useAppNavigate();

  const [isCertificateShown, setIsCertificateShown] = useState(false);
  const [areAnswersShown, setAreAnswersShown] = useState(false);

  const user = useAppSelector(({ auth }) => auth.user);

  const getQuestionsAndAnswersState = useGetQuestionsAndAnswers();

  const skillName = data?.title;
  const average = ((data?.mark || 0) * 100).toFixed(0);
  const takenTime = convertMsToHourMinSec(
    dayjs(data?.endedQuizAt).toDate().getTime() -
      dayjs(data?.startedQuizAt).toDate().getTime()
  );
  const correctAnswers = data?.correctAnsweredQuestionsCount;
  const totalAnswers = data?.totalQuestionsCount;

  return !areAnswersShown ? (
    <div className="w-full text-black dark:text-white">
      <div className="flex w-full flex-col items-center justify-center gap-y-10 p-5">
        <img
          src={"/assets/images/quiz-success-header.svg"}
          alt="congratulations"
          className="h-[104px] w-[276px]"
        />
        <TranslatedText
          text={`skill-details.quiz.${"you have successfully completed the test"}`}
          className="text-[22px] font-bold text-[#39BC94]"
        />
        <div className="flex w-full flex-col items-center justify-center gap-y-4">
          <div className="flex w-1/2 items-center justify-around text-[17px] font-medium">
            <div className="text-b2b-primary">{skillName}</div>
            <div className="flex items-center justify-center">
              <TranslatedText text={`skill-details.quiz.${"average"}`} />
              <div>: {average}%</div>
            </div>
          </div>
          <div className="flex w-1/2 items-center justify-around text-[17px] font-medium">
            <TranslatedText text={`skill-details.quiz.${"correct answers"}`} />
            <TranslatedText text={`skill-details.quiz.${"taken time"}`} />
          </div>
          <div className="flex w-1/2 items-center justify-around text-[17px] font-medium">
            <div>
              {correctAnswers}/{totalAnswers}
            </div>
            <div>{takenTime}</div>
          </div>
        </div>
        <div className="flex w-1/2 items-start justify-around">
          <div className="flex flex-col gap-y-2">
            <ShadcnButton
              onClick={() => {
                setIsCertificateShown(true);
              }}
              className="bg-b2b-primary text-white hover:bg-b2b-primary/80"
            >
              <TranslatedText
                text={`skill-details.quiz.${"view certificate"}`}
              />
            </ShadcnButton>
            <ShadcnButton
              className="bg-transparent text-b2b-primary"
              onClick={() => {
                if (data?.id) {
                  getQuestionsAndAnswers(data.id, () => {
                    setAreAnswersShown(true);
                  });
                }
              }}
              isLoading={getQuestionsAndAnswersState.status === "loading"}
            >
              <TranslatedText text={`skill-details.quiz.${"show answers"}`} />
            </ShadcnButton>
          </div>
          <ShadcnButton
            className="bg-[#898A8D] hover:bg-[#898A8D]/85"
            onClick={() => {
              navigate(`/details/${data?.contentEntityId}/`);
            }}
          >
            <TranslatedText text={`skill-details.quiz.${"back to skill"}`} />
          </ShadcnButton>
        </div>
      </div>
      {isCertificateShown ? (
        <div className="relative w-full">
          <div
            className="relative mx-auto flex flex-col items-center justify-center gap-y-2 rounded-lg bg-cover bg-center"
            style={{
              backgroundImage: "url('/assets/images/certificate-bg.svg')",
              width: "400px",
              height: "300px",
            }}
          >
            <img
              src="/assets/icons/logo.svg"
              alt="logo"
              className="absolute right-[25px] top-[25px] h-[57px] w-[49px]"
            />
            <div className="text-[33px] font-bold text-primary">
              شهادة مهارة
            </div>
            <div className="text-[18px] text-primary">هذه الشهادة منحت إلى</div>
            <div className="text-[21px] font-medium text-black">
              {user.name || user.email || user.phoneNumber}
            </div>
            <div className="text-[16px] text-black">
              وذلك بعد اجتيازه متطلبات المهارة بنجاح
            </div>
            <div className="text-[24px] font-medium text-primary">{`" ${skillName} "`}</div>
          </div>
        </div>
      ) : null}
    </div>
  ) : (
    <div className="w-full text-black dark:text-white">
      <div className="flex w-[900px] flex-col items-start justify-start gap-y-3">
        {getQuestionsAndAnswersState.data.map((d) => {
          return (
            <div key={d.questionId} className="w-full bg-[#D9D9D9]/20">
              <div className="px-12 py-6 text-[17px] font-bold text-b2b-primary">
                {d.text}
              </div>
              <div className="flex w-[800px] flex-col gap-y-5">
                {d.answers?.map((a, index) => (
                  <QuizOption
                    key={index}
                    no={index + 1}
                    text={a.text || ""}
                    isCorrect={a.isCorrect}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <div className="mx-auto mt-8">
        <ShadcnButton
          className="bg-[#898A8D] hover:bg-[#898A8D]/85"
          onClick={() => {
            setAreAnswersShown(false);
          }}
        >
          <TranslatedText text={`skill-details.quiz.${"back to result"}`} />
        </ShadcnButton>
      </div>
    </div>
  );
};
